import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdvanceTableResponsive from '../../../components/common/advance-table/AdvanceTableResponsive';
import ApprovalRejectReasonModal from '../../../components/modal/ApprovalRejectReasonModal';
import PendingListEmptyState from './common/PendingListEmptyState';
import { PendingKeywordHighlight } from './common/PendingKeywordHighlight';
import PendingListTableFooter from './common/PendingListTableFooter';
import PendingListToolbar from './common/PendingListToolbar';
import { formatApprovalTriggerSummary, getApprovalTriggerSummaryLines, getPendingApprovalKind } from './common/pendingApprovalTriggerSummary';
import { getPendingListApplicant, isPendingListApplicantHighlighted } from '../../../utils/approval/approvalListHub';
import formatCurrency from '../../../utils/formatCurrency';
import { getCityNameByCode } from '../../../utils/cityUtils';
import CardDropdown from '../../../components/common/CardDropdown';
import useAdvanceTable from '../../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../../providers/AdvanceTableProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { socket } from '../../../socket';
import PendingListLoadingState from './common/PendingListLoadingState';
import PendingListStatusBadge, { PendingThemeBadge } from './common/PendingListStatusBadge';
import { PENDING_TAB_BADGE, PENDING_TABLE_PER_PAGE } from './common/pendingTableConfig';
import { buildPendingApplyAtColumn, PENDING_APPLY_AT_SORT } from './common/pendingApplyAtColumn';
import { usePendingKeywordFilter } from './common/usePendingKeywordFilter';
import { useUpdateCustomerApprovalByIdMutation } from '../../../slices/approvalsApiSlice';
import { useCheckCustomerNameMutation, useCreateCustomerMutation } from '../../../slices/customersApiSlice';
import { useNotifyCustomerApprovedToApplicantMutation } from '../../../slices/notificationsApiSlice';
import { PERM, hasPermission } from '../../../utils/accessPermissions';
import ApproveModal from './modal/ApproveModal';
import CustomerApplyInfoModal from './modal/CustomerApplyInfoModal';
import FinalCostApplyInfoModal from './modal/FinalCostApplyInfoModal';
import QuotaApplyInfoModal from './modal/QuotaApplyInfoModal';

const RESOLVE_HIGHLIGHT_MS = 360;
const RESOLVE_LEAVE_MS = 420;
const RECENT_ACTION_BANNER_MS = 3200;
const DEBOUNCE_DELAY = 400;

const ApprovingOrderPendings = () => {
	const { userInfo } = useSelector((state) => state.auth);
	const canExecuteCustomerApproval = hasPermission(userInfo, PERM.APPROVAL_CUSTOMER_EXECUTE);
	const canExecuteQuotaApproval = hasPermission(userInfo, PERM.APPROVAL_QUOTA_EXECUTE);
	const [isOpenPendingModal, setIsOpenPendingModal] = useState(false);
	const [customerApplyInfo, setCustomerApplyInfo] = useState(null);
	const [quotaApplyInfo, setQuotaApplyInfo] = useState(null);
	const [finalCostApplyInfo, setFinalCostApplyInfo] = useState(null);
	const [showCustomerRejectConfirm, setShowCustomerRejectConfirm] = useState(false);
	const [showQuotaRejectConfirm, setShowQuotaRejectConfirm] = useState(false);
	const [isCustomerPassing, setIsCustomerPassing] = useState(false);
	const [isCustomerRejecting, setIsCustomerRejecting] = useState(false);
	const [isQuotaPassing, setIsQuotaPassing] = useState(false);
	const [isQuotaRejecting, setIsQuotaRejecting] = useState(false);
	const [justResolved, setJustResolved] = useState(null);
	const [resolvedSnapshot, setResolvedSnapshot] = useState(null);
	const [recentAction, setRecentAction] = useState(null);
	const resolveTimersRef = useRef([]);
	const recentActionTimerRef = useRef(null);
	const {
		myApprovals = [],
		isLoadingMyApprovals,
		isMyApprovalsOrderFetching,
		isApprovalsUninitialized,
		setCurrentOrderOIdState,
		refetchMyApprovals,
		refetchProcessingOrderLists,
	} = useOrderContext();

	const [updateCustomerApprovalStatus] = useUpdateCustomerApprovalByIdMutation();
	const [notifyCustomerApprovedToApplicant] = useNotifyCustomerApprovedToApplicantMutation();
	const [checkCustomerName] = useCheckCustomerNameMutation();
	const [createCustomer] = useCreateCustomerMutation();

	useEffect(() => {
		return () => {
			resolveTimersRef.current.forEach((timerId) => clearTimeout(timerId));
			resolveTimersRef.current = [];
			if (recentActionTimerRef.current) {
				clearTimeout(recentActionTimerRef.current);
			}
		};
	}, []);

	const clearResolveTimers = useCallback(() => {
		resolveTimersRef.current.forEach((timerId) => clearTimeout(timerId));
		resolveTimersRef.current = [];
	}, []);

	const displayApprovals = useMemo(() => {
		if (!justResolved || !resolvedSnapshot) {
			return myApprovals;
		}
		const orderOid = String(justResolved.orderOid);
		const stillInList = myApprovals.some((order) => String(order?._id) === orderOid);
		if (stillInList) {
			return myApprovals;
		}
		return [resolvedSnapshot, ...myApprovals];
	}, [justResolved, resolvedSnapshot, myApprovals]);

	const { keyword, hasKeyword, filteredOrders } = usePendingKeywordFilter(displayApprovals);
	const handleApprovalResolved = useCallback(
		({ orderOid, orderId, result }) => {
			if (!orderOid) {
				refetchProcessingOrderLists?.();
				refetchMyApprovals?.();
				return;
			}

			const oid = String(orderOid);
			const snapshot = myApprovals.find((order) => String(order?._id) === oid) ?? null;
			const displayOrderId = orderId || snapshot?.orderId || snapshot?.approval?.detail?.orderId || '';

			clearResolveTimers();
			if (recentActionTimerRef.current) {
				clearTimeout(recentActionTimerRef.current);
			}

			setResolvedSnapshot(snapshot);
			setJustResolved({ orderOid: oid, result, phase: 'highlight' });
			setRecentAction({ orderId: displayOrderId, result });

			const leaveTimer = setTimeout(() => {
				setJustResolved((prev) => (prev && String(prev.orderOid) === oid ? { ...prev, phase: 'leaving' } : prev));
			}, RESOLVE_HIGHLIGHT_MS);

			const doneTimer = setTimeout(() => {
				setJustResolved(null);
				setResolvedSnapshot(null);
				refetchProcessingOrderLists?.();
				refetchMyApprovals?.();
			}, RESOLVE_HIGHLIGHT_MS + RESOLVE_LEAVE_MS);

			recentActionTimerRef.current = setTimeout(() => {
				setRecentAction(null);
				recentActionTimerRef.current = null;
			}, RECENT_ACTION_BANNER_MS);

			resolveTimersRef.current = [leaveTimer, doneTimer];
		},
		[clearResolveTimers, myApprovals, refetchMyApprovals, refetchProcessingOrderLists],
	);

	const handleApprovalClick = useCallback(
		(row) => {
			const rowId = row?._id;
			if (!rowId) return;
			if (justResolved && String(justResolved.orderOid) === String(rowId)) {
				return;
			}
			if (row.entryKind === 'customer') {
				setCustomerApplyInfo({
					approval: row.approval || {
						title: row.title,
						status: row.approvalStatus,
						detail: { newCustomerConcatName: row.customerInfo?.customerName || '' },
						applicant: row.createdUser,
					},
				});
				return;
			}
			if (row.entryKind === 'quota') {
				setQuotaApplyInfo({
					approval: row.approval || {
						title: row.title,
						status: row.approvalStatus,
						detail: { customerName: row.customerInfo?.customerName || '' },
						applicant: row.createdUser,
						assistant: row.assistantUser,
						_id: rowId,
					},
				});
				return;
			}
			if (row.entryKind === 'finalCost') {
				setFinalCostApplyInfo({
					approval: row.approval || {
						title: row.title,
						status: row.approvalStatus,
						detail: { orderId: row.orderId },
						applicant: row.createdUser,
						assistant: row.assistantUser,
						_id: rowId,
					},
				});
				return;
			}
			setIsOpenPendingModal(true);
			setCurrentOrderOIdState(rowId);
			socket.emit('join order', rowId);
		},
		[justResolved, setCurrentOrderOIdState],
	);

	const handleCustomerApprove = useCallback(async () => {
		const approval = customerApplyInfo?.approval;
		const approvalId = approval?._id;
		if (!approvalId || isCustomerPassing) return;
		setIsCustomerPassing(true);
		try {
			await checkCustomerName({
				newCustomerType: approval.detail?.newCustomerType,
				newCustomerName: approval.detail?.newCustomerConcatName,
			}).unwrap();

			const approved_res = await updateCustomerApprovalStatus({
				_id: approvalId,
				status: 'approved',
			}).unwrap();

			const { approver, applicant, detail, _id } = approved_res;
			const createdCustomer = await createCustomer({
				type: detail?.newCustomerType,
				name: detail?.newCustomerConcatName,
				organization: detail?.newCustomerOrganization,
				department: detail?.newCustomerDepartment,
				group: detail?.newCustomerGroup,
				city: detail?.newCustomerCity,
				address: detail?.newCustomerAddress,
				phone: detail?.newCustomerPhone,
				email: detail?.newCustomerEmail,
				applicant,
			}).unwrap();

			toast.success(`${createdCustomer.name} 客户创建成功`);
			await notifyCustomerApprovedToApplicant({
				toUser: applicant,
				fromUser: approver,
				content: { message_text: `新增客户申请已通过`, emoji: '✅', detail, approvalId: _id },
			}).unwrap();

			setCustomerApplyInfo(null);
			handleApprovalResolved({
				orderOid: approvalId,
				orderId: detail?.newCustomerConcatName || approval.title || '',
				result: 'approved',
			});
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '审批操作失败，请稍后重试');
		} finally {
			setTimeout(() => setIsCustomerPassing(false), DEBOUNCE_DELAY);
		}
	}, [
		checkCustomerName,
		createCustomer,
		customerApplyInfo,
		handleApprovalResolved,
		isCustomerPassing,
		notifyCustomerApprovedToApplicant,
		updateCustomerApprovalStatus,
	]);

	const handleCustomerReject = useCallback(async (rejectReason) => {
		const approval = customerApplyInfo?.approval;
		const approvalId = approval?._id;
		const approvalSuggestions = String(rejectReason || '').trim();
		if (!approvalId || isCustomerRejecting) return;
		if (!approvalSuggestions) {
			toast.error('请填写驳回原因');
			return;
		}
		setIsCustomerRejecting(true);
		try {
			const approved_res = await updateCustomerApprovalStatus({
				_id: approvalId,
				status: 'reject',
				approvalSuggestions,
			}).unwrap();

			const { approver, applicant, detail, _id } = approved_res;
			await notifyCustomerApprovedToApplicant({
				toUser: applicant,
				fromUser: approver,
				content: { message_text: `新增客户申请未通过`, emoji: '❌', detail, approvalId: _id },
			}).unwrap();

			toast.success('申请已驳回');
			setShowCustomerRejectConfirm(false);
			setCustomerApplyInfo(null);
			handleApprovalResolved({
				orderOid: approvalId,
				orderId: detail?.newCustomerConcatName || approval.title || '',
				result: 'rejected',
			});
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '驳回操作失败，请稍后重试');
		} finally {
			setTimeout(() => setIsCustomerRejecting(false), DEBOUNCE_DELAY);
		}
	}, [
		customerApplyInfo,
		handleApprovalResolved,
		isCustomerRejecting,
		notifyCustomerApprovedToApplicant,
		updateCustomerApprovalStatus,
	]);

	const handleQuotaApprove = useCallback(async () => {
		const approval = quotaApplyInfo?.approval;
		const approvalId = approval?._id;
		if (!approvalId || isQuotaPassing) return;
		setIsQuotaPassing(true);
		try {
			await updateCustomerApprovalStatus({
				_id: approvalId,
				status: 'approved',
			}).unwrap();
			toast.success('审批通过');
			setQuotaApplyInfo(null);
			handleApprovalResolved({
				orderOid: approvalId,
				orderId: approval.detail?.customerName || approval.title || '',
				result: 'approved',
			});
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '审批操作失败，请稍后重试');
		} finally {
			setTimeout(() => setIsQuotaPassing(false), DEBOUNCE_DELAY);
		}
	}, [handleApprovalResolved, isQuotaPassing, quotaApplyInfo, updateCustomerApprovalStatus]);

	const handleQuotaReject = useCallback(
		async (rejectReason) => {
			const approval = quotaApplyInfo?.approval;
			const approvalId = approval?._id;
			const approvalSuggestions = String(rejectReason || '').trim();
			if (!approvalId || isQuotaRejecting) return;
			if (!approvalSuggestions) {
				toast.error('请填写驳回原因');
				return;
			}
			setIsQuotaRejecting(true);
			try {
				await updateCustomerApprovalStatus({
					_id: approvalId,
					status: 'reject',
					approvalSuggestions,
				}).unwrap();
				toast.success('申请已驳回');
				setShowQuotaRejectConfirm(false);
				setQuotaApplyInfo(null);
				handleApprovalResolved({
					orderOid: approvalId,
					orderId: approval.detail?.customerName || approval.title || '',
					result: 'rejected',
				});
			} catch (error) {
				toast.error(error?.data?.message || error?.error || '驳回操作失败，请稍后重试');
			} finally {
				setTimeout(() => setIsQuotaRejecting(false), DEBOUNCE_DELAY);
			}
		},
		[handleApprovalResolved, isQuotaRejecting, quotaApplyInfo, updateCustomerApprovalStatus],
	);

	const getResolvedRowClassName = useCallback(
		(row) => {
			if (!justResolved || String(row?._id) !== String(justResolved.orderOid)) {
				return undefined;
			}
			const resultClass = justResolved.result === 'approved' ? 'approving-pending-row--resolved-approved' : 'approving-pending-row--resolved-rejected';
			return justResolved.phase === 'leaving' ? `${resultClass} approving-pending-row--leaving` : resultClass;
		},
		[justResolved],
	);

	const formatDateTime = useCallback((date) => {
		return moment(date).format('YYYY-MM-DD HH:mm');
	}, []);

	const columns = useMemo(
		() => [
			{
				id: 'approvalTitle',
				accessorFn: (row) => {
					const kind = getPendingApprovalKind(row);
					if (row?.entryKind === 'quota') {
						const applyQuota = row?.approval?.detail?.applyQuota;
						const amountText = applyQuota != null && applyQuota !== '' ? formatCurrency(applyQuota) : '';
						return [kind.label, kind.subject, amountText ? `申请额度 ${amountText}` : ''].filter(Boolean).join(' ');
					}
					if (row?.entryKind === 'customer') {
						const cityCode = String(row?.approval?.detail?.newCustomerCity || '').trim();
						const cityName = cityCode ? getCityNameByCode(cityCode) : '';
						return [kind.label, kind.subject, cityName ? `城市 ${cityName}` : ''].filter(Boolean).join(' ');
					}
					const triggerSummary = formatApprovalTriggerSummary(row?.approval);
					return [kind.label, kind.subject, triggerSummary].filter(Boolean).join(' ');
				},
				header: '审批事项',
				meta: {
					card: { role: 'primary' },
					headerProps: { className: 'sticky-column text-900 text-start' },
					cellProps: {
						className: 'fw-semibold text-start',
					},
				},
				cell: ({ row: { original } }) => {
					const { approval, entryKind } = original;
					const kind = getPendingApprovalKind(original);
					const applyQuota = entryKind === 'quota' ? approval?.detail?.applyQuota : null;
					const quotaAmountText = applyQuota != null && applyQuota !== '' ? formatCurrency(applyQuota) : '';
					const customerCityCode = entryKind === 'customer' ? String(approval?.detail?.newCustomerCity || '').trim() : '';
					const customerCity = customerCityCode ? getCityNameByCode(customerCityCode) : '';
					const triggerLines =
						entryKind === 'customer' || entryKind === 'quota' || kind.label === '成本修正' ? [] : getApprovalTriggerSummaryLines(approval);
					return (
						<div className='d-flex flex-column align-items-start gap-1'>
							<div className='pending-approval-kind-line gap-2'>
								<PendingThemeBadge bg={kind.bg} text={kind.label} pill className='flex-shrink-0' />
								<Link
									onClick={(e) => {
										e.preventDefault();
										handleApprovalClick(original);
									}}
									className={`link-${PENDING_TAB_BADGE.approving} text-decoration-none`}
									title={kind.subject || undefined}
									to='#'
								>
									<PendingKeywordHighlight text={kind.subject} />
								</Link>
							</div>
							{quotaAmountText ? (
								<span className='fs-11 fw-normal text-600'>
									申请额度 <PendingKeywordHighlight text={quotaAmountText} />
								</span>
							) : null}
							{customerCity ? (
								<span className='fs-11 fw-normal text-600'>
									城市：
									<PendingKeywordHighlight text={customerCity} />
								</span>
							) : null}
							{triggerLines.length > 0 ? (
								<span className='fs-11 fw-normal text-600 text-wrap' style={{ maxWidth: '18rem', whiteSpace: 'normal' }}>
									{triggerLines.map((line, index) => (
										<span key={line}>
											{index > 0 ? '；' : null}
											<PendingKeywordHighlight text={line} />
										</span>
									))}
								</span>
							) : null}
						</div>
					);
				},
			},
			{
				accessorKey: 'customerInfo.customerName',
				header: '客户名称',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => {
					const { customerInfo } = original;
					return (
						<span className={`link-${PENDING_TAB_BADGE.approving}`}>
							<PendingKeywordHighlight text={customerInfo?.customerName} />
						</span>
					);
				},
			},
			{
				accessorKey: 'approvalStatus',
				header: '审批状态',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
					card: { role: 'status' },
				},
				cell: ({ row: { original } }) => {
					if (justResolved && String(original?._id) === String(justResolved.orderOid)) {
						if (justResolved.result === 'approved') {
							return <PendingListStatusBadge bg='success' text='已通过' showIcon={false} />;
						}
						return <PendingListStatusBadge bg='danger' text='已驳回' showIcon={false} />;
					}
					return <PendingListStatusBadge bg={PENDING_TAB_BADGE.approving} text='待审批' showIcon={false} />;
				},
			},
			{
				id: 'saleName',
				accessorFn: (row) => {
					if (row?.entryKind === 'customer' || row?.entryKind === 'quota') return row?.createdUser?.name || '';
					return row?.saleUserInfo?.name || row?.saleUser?.name || '';
				},
				header: '销售员',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ getValue }) => <span className='fs-11'>{getValue() || '-'}</span>,
			},
			{
				accessorKey: 'assistantName',
				header: '销售助理',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ row: { original } }) => <span className='fs-11'>{original.assistantUser?.name || '-'}</span>,
			},
			{
				id: 'applicantName',
				accessorFn: (row) => getPendingListApplicant(row)?.name || '',
				header: '申请人',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ row: { original } }) => {
					const applicant = getPendingListApplicant(original);
					return (
						<span className={isPendingListApplicantHighlighted(original) ? 'fs-11 text-primary' : 'fs-11'}>{applicant?.name || '-'}</span>
					);
				},
			},
			buildPendingApplyAtColumn(formatDateTime, '申请时间'),
			{
				accessorKey: 'actions',
				header: '',
				enableSorting: false,
				meta: {
					cellProps: {
						className: 'text-end py-2',
					},
				},
				cell: ({ row: { original } }) => {
					const { approval, entryKind } = original;
					if (entryKind === 'customer' || entryKind === 'quota' || entryKind === 'finalCost') {
						return (
							<CardDropdown drop='start'>
								<div className='py-2'>
									<Dropdown.Item as='button' type='button' onClick={() => handleApprovalClick(original)}>
										查看
									</Dropdown.Item>
								</div>
							</CardDropdown>
						);
					}
					const detailUrl = approval?.detail?.orderOID && approval?._id ? `/approval/order/${approval.detail.orderOID}/detail/${approval._id}` : '#';

					return (
						<CardDropdown drop='start'>
							<div className='py-2'>
								<Dropdown.Item href={detailUrl}>查看</Dropdown.Item>
								<Dropdown.Item as='button' type='button' onClick={() => handleApprovalClick(original)}>
									快速审批
								</Dropdown.Item>
							</div>
						</CardDropdown>
					);
				},
			},
		],
		[handleApprovalClick, formatDateTime, justResolved],
	);

	const table = useAdvanceTable({
		data: filteredOrders,
		columns,
		sortable: true,
		pagination: true,
		perPage: PENDING_TABLE_PER_PAGE,
		initialState: {
			sorting: PENDING_APPLY_AT_SORT,
		},
	});

	if (isApprovalsUninitialized || isLoadingMyApprovals || (isMyApprovalsOrderFetching && myApprovals.length === 0)) {
		return <PendingListLoadingState />;
	}

	const recentActionBanner = recentAction ? (
		<div
			className={`approving-recent-action approving-recent-action--${recentAction.result === 'approved' ? 'approved' : 'rejected'}`}
			role='status'
			aria-live='polite'
		>
			<FontAwesomeIcon icon={recentAction.result === 'approved' ? 'circle-check' : 'circle-xmark'} className='approving-recent-action__icon' />
			<span className='approving-recent-action__text'>
				刚刚处理：{recentAction.orderId ? `#${recentAction.orderId}` : '订单'}
				<span className='approving-recent-action__sep'>·</span>
				{recentAction.result === 'approved' ? '审批通过' : '已驳回'}
			</span>
		</div>
	) : null;

	const showFooter = displayApprovals.length > 0 || hasKeyword;

	return (
		<>
			<AdvanceTableProvider {...table}>
				<PendingListToolbar leading={recentActionBanner}>
					{filteredOrders.length === 0 ? (
						<PendingListEmptyState
							emptyTitle={hasKeyword ? '无匹配项' : '暂无待审批'}
							emptyDescription={hasKeyword ? `当前待办中没有包含「${keyword}」的记录。` : '目前没有需要你处理的订单、客户或额度审批。'}
						/>
					) : (
						<AdvanceTableResponsive
							headerClassName='bg-200 text-nowrap align-middle font-sans-serif'
							rowClassName='btn-reveal-trigger text-nowrap align-middle'
							getRowClassName={getResolvedRowClassName}
							tableProps={{
								className: 'fs-10 fw-semibold mb-0 overflow-hidden',
							}}
						/>
					)}
					{showFooter ? <PendingListTableFooter showTableControls={filteredOrders.length > 0} /> : null}
				</PendingListToolbar>
			</AdvanceTableProvider>
			<ApproveModal
				setIsOpenPendingModal={setIsOpenPendingModal}
				isOpenPendingModal={isOpenPendingModal}
				onApprovalResolved={handleApprovalResolved}
			/>
			<CustomerApplyInfoModal
				show={Boolean(customerApplyInfo)}
				onHide={() => setCustomerApplyInfo(null)}
				approval={customerApplyInfo?.approval}
				onApprove={canExecuteCustomerApproval ? handleCustomerApprove : undefined}
				onReject={canExecuteCustomerApproval ? () => setShowCustomerRejectConfirm(true) : undefined}
				isApproving={isCustomerPassing}
				isRejecting={isCustomerRejecting}
			/>
			<QuotaApplyInfoModal
				show={Boolean(quotaApplyInfo)}
				onHide={() => setQuotaApplyInfo(null)}
				approval={quotaApplyInfo?.approval}
				onApprove={canExecuteQuotaApproval ? handleQuotaApprove : undefined}
				onReject={canExecuteQuotaApproval ? () => setShowQuotaRejectConfirm(true) : undefined}
				isApproving={isQuotaPassing}
				isRejecting={isQuotaRejecting}
			/>
			<FinalCostApplyInfoModal
				show={Boolean(finalCostApplyInfo)}
				onHide={() => setFinalCostApplyInfo(null)}
				approval={finalCostApplyInfo?.approval}
				onResolved={({ approvalId, orderId, result }) => {
					setFinalCostApplyInfo(null);
					handleApprovalResolved({ orderOid: approvalId, orderId, result });
				}}
			/>
			<ApprovalRejectReasonModal
				show={showCustomerRejectConfirm}
				onHide={() => setShowCustomerRejectConfirm(false)}
				onConfirm={handleCustomerReject}
				confirmWaiting={isCustomerRejecting}
				hint='驳回后申请人可在再次申请客户时看到此说明，请简明写清原因。'
				placeholder='例如：单位名称不规范、部门信息不完整、联系方式有误…'
			/>
			<ApprovalRejectReasonModal
				show={showQuotaRejectConfirm}
				onHide={() => setShowQuotaRejectConfirm(false)}
				onConfirm={handleQuotaReject}
				confirmWaiting={isQuotaRejecting}
				hint='驳回后申请人可在再次申请额度时看到此说明，请简明写清原因。'
				placeholder='例如：申请金额超出可批范围、账期与约定不符、说明不充分…'
			/>
		</>
	);
};

export default ApprovingOrderPendings;
